import { ListFilterParams } from "components/typings";
import { OfferListTable } from "./OfferListTable";
import Button from "components/buttons/Button";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { OfferListType, OFFER_LIST_TYPE, Offer } from "pages/project/offer/offer.typing";
import { getCatToolName, TaskBase } from "pages/project/common/taskBase.typing";
import Badge from "components/Badge";
import Collapse from "components/Collapse";
import AcceptOffersModal from "./modals/AcceptOffersModal";
import DeclineOffersModal from "./modals/DeclineOffersModal";

interface GroupedOfferListTableProps {
  offers: Offer[];
  shownDetailDataId?: string;
  filterParams: ListFilterParams<TaskBase>;
  type: OfferListType;
}

export function GroupedOfferListTable({ offers, shownDetailDataId, filterParams, type }: GroupedOfferListTableProps) {
  const { t } = useTranslation();

  // Group offers by customer name, CAT tool, task type, and language pair
  // Special case: translation and qc-rev-1 offers with useTep = true are only grouped together if both types exist with useTep = true
  const groupedOffers = offers.reduce(
    (acc, offer) => {
      const catToolName = getCatToolName(offer.catToolId) || t("task-base.fields.noCatTool");
      const languagePair = `${offer.sourceLanguage} → ${offer.targetLanguage}`;

      let groupTaskType = offer.taskType;

      // Check if this is a translation or qc-rev-1 offer with useTep
      if (offer.useTep && (offer.taskType === "Translation" || offer.taskType === "QC 1-REV_ALL")) {
        // Check if there are offers of the other type in the same customer/language pair/catTool with useTep
        const otherType = offer.taskType === "Translation" ? "QC 1-REV_ALL" : "Translation";
        const hasOtherTypeWithTep = offers.some(
          (o) =>
            o.taskType === otherType &&
            o.useTep &&
            o.customerName === offer.customerName &&
            o.sourceLanguage === offer.sourceLanguage &&
            o.targetLanguage === offer.targetLanguage &&
            o.catToolId === offer.catToolId
        );

        if (hasOtherTypeWithTep) {
          groupTaskType = "TEP";
        }
      }

      const groupKey = `${offer.customerName}|${catToolName}|${groupTaskType}|${languagePair}`;

      if (!acc[groupKey]) {
        acc[groupKey] = {
          customerName: offer.customerName,
          catTool: catToolName,
          taskType: groupTaskType,
          languagePair,
          offers: [],
        };
      }
      acc[groupKey].offers.push(offer);
      return acc;
    },
    {} as Record<
      string,
      { customerName: string; catTool: string; taskType: string; languagePair: string; offers: Offer[] }
    >
  );

  const [selectedOffersByGroup, setSelectedOffersByGroup] = useState<Record<string, string[]>>({});
  const [modalAction, setModalAction] = useState<"accept" | "decline" | null>(null);
  const [selectedOfferIds, setSelectedOfferIds] = useState<string[]>([]);

  const handleSelectionChange = (groupKey: string, selectedOfferIds: string[]) => {
    setSelectedOffersByGroup((prev) => ({
      ...prev,
      [groupKey]: selectedOfferIds,
    }));
  };

  function handleAcceptOffers(groupKey: string) {
    const selectedOffers = selectedOffersByGroup[groupKey] || [];
    setSelectedOfferIds(selectedOffers);
    setModalAction("accept");
  }

  function handleRejectOffers(groupKey: string) {
    const selectedOffers = selectedOffersByGroup[groupKey] || [];
    setSelectedOfferIds(selectedOffers);
    setModalAction("decline");
  }

  const handleModalClose = () => {
    setModalAction(null);
    setSelectedOfferIds([]);
  };

  return (
    <div className="space-y-6">
      {Object.entries(groupedOffers).map(([groupKey, groupData]) => (
        <div
          key={groupKey}
          className="text-color-base bg-color-base rounded-md border border-slate-200 p-4 dark:border-slate-800"
        >
          <Collapse
            header={
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-4">
                  <h3 className="text-lg font-semibold">{groupData.customerName}</h3>
                  <div className="flex gap-2">
                    <Badge className="text-xs font-semibold">{groupData.catTool}</Badge>
                    <Badge className="text-xs font-semibold">{groupData.taskType}</Badge>
                    <Badge className="text-xs font-semibold">{groupData.languagePair}</Badge>
                  </div>
                </div>

                {type === OFFER_LIST_TYPE.pending && (
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      color="ternary"
                      label={
                        (selectedOffersByGroup[groupKey]?.length ?? 0) !== groupData.offers.length
                          ? t("offer.rejectSomeOffers", {
                              selectedOffersCount: selectedOffersByGroup[groupKey]?.length ?? 0,
                              totalOffersCount: groupData.offers.length,
                            })
                          : t("offer.rejectAllOffers", { count: groupData.offers.length })
                      }
                      onClick={() => handleRejectOffers(groupKey)}
                      disabled={!selectedOffersByGroup[groupKey]?.length}
                    />
                    <Button
                      color="primary"
                      label={
                        (selectedOffersByGroup[groupKey]?.length ?? 0) !== groupData.offers.length
                          ? t("offer.acceptSomeOffers", {
                              selectedOffersCount: selectedOffersByGroup[groupKey]?.length ?? 0,
                              totalOffersCount: groupData.offers.length,
                            })
                          : t("offer.acceptAllOffers", { count: groupData.offers.length })
                      }
                      onClick={() => handleAcceptOffers(groupKey)}
                      disabled={!selectedOffersByGroup[groupKey]?.length}
                    />
                  </div>
                )}
              </div>
            }
            defaultOpen={true}
          >
            <div className="mt-4">
              <OfferListTable
                key={groupKey}
                groupTaskType={groupData.taskType}
                offers={groupData.offers}
                shownDetailDataId={shownDetailDataId}
                filterParams={filterParams}
                canSelect={type === OFFER_LIST_TYPE.pending}
                onSelectionChange={(selectedIds) => handleSelectionChange(groupKey, selectedIds)}
              />
            </div>
          </Collapse>
        </div>
      ))}
      <AcceptOffersModal show={modalAction === "accept"} offerIds={selectedOfferIds} onClose={handleModalClose} />
      <DeclineOffersModal show={modalAction === "decline"} offerIds={selectedOfferIds} onClose={handleModalClose} />
    </div>
  );
}
